import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
    Loader2, CheckCircle, Search, Minimize2, Layout, Play, Settings2,
    Brain, Award, Sparkles, ChevronRight
} from 'lucide-react';
import { toast, Toaster } from 'sonner';
import { SENDAFooter } from '../components/SENDAFooter';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CH5_MODULES = [
    { id: 'detector_friccion', name: 'Detector de fricción', icon: Search, color: '#E8B27A' },
    { id: 'vmv', name: 'Versión mínima viable', icon: Minimize2, color: '#5FAF9D' },
    { id: 'diseno_entorno', name: 'Diseño de entorno', icon: Layout, color: '#3E5F6E' },
    { id: 'arranque_obligatorio', name: 'Arranque obligatorio', icon: Play, color: '#7A9BA8' },
    { id: 'sistema_ejecucion', name: 'Sistema de ejecución', icon: Settings2, color: '#E8B27A' }
];

export default function ResumenCapitulo5() {
    const navigate = useNavigate();
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchProfile = async () => {
            try {
                const { data } = await axios.get(`${API_URL}/api/user/profile`, { withCredentials: true });
                setProfile(data);
            } catch {
                toast.error('Error al cargar datos');
                navigate('/usuario');
            } finally { setLoading(false); }
        };
        fetchProfile();
    }, [navigate]);

    if (loading) return <div className="min-h-screen bg-[#F5F7F8] flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#3E5F6E]" /></div>;

    const completedModules = profile?.modules_completed || [];
    const ch5Completed = CH5_MODULES.filter(m => completedModules.some(cm => cm.module_id === m.id));
    const completionRate = (ch5Completed.length / CH5_MODULES.length) * 100;
    const aiSummaries = profile?.ai_summaries || [];

    const friccionSummary = aiSummaries.find(s => s.module === 'detector_friccion');
    const vmvSummary = aiSummaries.find(s => s.module === 'vmv');
    const sistemaEjecucionSummary = aiSummaries.find(s => s.module === 'sistema_ejecucion');
    const executionSystem = profile?.execution_system;

    return (
        <div className="min-h-screen bg-[#F5F7F8]">
            <Toaster position="top-right" richColors />
            <header className="bg-white/90 backdrop-blur-xl border-b border-[#DCE3E8]/50 sticky top-0 z-50">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <img src="/logo-senda.png" alt="SENDA" className="h-9 w-auto" />
                            <div>
                                <h1 className="text-xl font-semibold text-[#1F2A30] font-['Outfit']" data-testid="ch5-summary-title">Resumen Capítulo 5</h1>
                                <p className="text-sm text-[#4A5B63]">Ejecución sin fricción</p>
                            </div>
                        </div>
                        <Link to="/usuario"><Button variant="ghost" className="text-[#3E5F6E] hover:bg-[#3E5F6E]/5">Volver al inicio</Button></Link>
                    </div>
                </div>
            </header>

            <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Hero */}
                <div className="relative overflow-hidden bg-gradient-to-br from-[#E8B27A] via-[#D4A06A] to-[#C4905A] rounded-3xl p-8 lg:p-12 mb-8 shadow-2xl">
                    <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl" />
                    <div className="absolute bottom-0 left-0 w-48 h-48 bg-[#3E5F6E]/10 rounded-full blur-3xl" />
                    <div className="relative z-10 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
                        <div className="flex-1">
                            <div className="flex items-center gap-2 mb-4">
                                <Award className="h-6 w-6 text-white" />
                                <span className="text-white font-medium uppercase tracking-wide text-sm">Capítulo 5 Completado</span>
                            </div>
                            <h2 className="text-3xl lg:text-4xl font-light text-white font-['Outfit'] mb-4">Tu sistema de ejecución está armado</h2>
                            <p className="text-white/70 text-lg max-w-xl">Has eliminado fricciones, definido tu versión mínima, preparado tu entorno y creado un disparador automático de acción.</p>
                        </div>
                        <div className="flex flex-col items-center">
                            <div className="relative w-40 h-40">
                                <svg className="w-full h-full transform -rotate-90">
                                    <circle cx="80" cy="80" r="70" fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="12" />
                                    <circle cx="80" cy="80" r="70" fill="none" stroke="#fff" strokeWidth="12" strokeLinecap="round" strokeDasharray={`${completionRate * 4.4} 440`} className="transition-all duration-1000" />
                                </svg>
                                <div className="absolute inset-0 flex flex-col items-center justify-center">
                                    <span className="text-4xl font-bold text-white font-['Outfit']">{Math.round(completionRate)}%</span>
                                    <span className="text-white/50 text-sm">completado</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
                    {/* Module Progress */}
                    <Card className="bg-white border-[#DCE3E8] shadow-sm">
                        <CardHeader><CardTitle className="flex items-center gap-2 text-[#1F2A30]"><Settings2 className="h-5 w-5 text-[#E8B27A]" />Progreso por módulo</CardTitle></CardHeader>
                        <CardContent>
                            <div className="space-y-4">
                                {CH5_MODULES.map((module) => {
                                    const isCompleted = completedModules.some(cm => cm.module_id === module.id);
                                    const Icon = module.icon;
                                    return (
                                        <div key={module.id} className="flex items-center gap-4" data-testid={`ch5-module-${module.id}`}>
                                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isCompleted ? 'bg-[#5FAF9D]' : 'bg-[#DCE3E8]'}`}>
                                                {isCompleted ? <CheckCircle className="h-5 w-5 text-white" /> : <Icon className="h-5 w-5 text-[#7A8A93]" />}
                                            </div>
                                            <div className="flex-1">
                                                <div className="flex items-center justify-between mb-1">
                                                    <span className={`text-sm font-medium ${isCompleted ? 'text-[#1F2A30]' : 'text-[#7A8A93]'}`}>{module.name}</span>
                                                    <span className={`text-xs ${isCompleted ? 'text-[#5FAF9D]' : 'text-[#7A8A93]'}`}>{isCompleted ? '100%' : '0%'}</span>
                                                </div>
                                                <div className="h-2 bg-[#DCE3E8] rounded-full overflow-hidden">
                                                    <div className="h-full bg-[#5FAF9D] transition-all duration-500" style={{ width: isCompleted ? '100%' : '0%' }} />
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </CardContent>
                    </Card>

                    {/* Key Insights */}
                    <Card className="bg-white border-[#DCE3E8] shadow-sm">
                        <CardHeader><CardTitle className="flex items-center gap-2 text-[#1F2A30]"><Sparkles className="h-5 w-5 text-[#E8B27A]" />Hallazgos clave</CardTitle></CardHeader>
                        <CardContent className="space-y-3">
                            {friccionSummary?.diagnostico && (
                                <div className="p-3 bg-[#F5F7F8] rounded-xl border border-[#DCE3E8]">
                                    <p className="text-xs font-semibold text-[#E8B27A] uppercase mb-1">Análisis de fricción</p>
                                    <p className="text-sm text-[#1F2A30] italic">"{friccionSummary.diagnostico}"</p>
                                </div>
                            )}
                            {vmvSummary?.insight_usuario && (
                                <div className="p-3 bg-[#F5F7F8] rounded-xl border border-[#DCE3E8]">
                                    <p className="text-xs font-semibold text-[#5FAF9D] uppercase mb-1">Versión mínima</p>
                                    <p className="text-sm text-[#1F2A30] italic">"{vmvSummary.insight_usuario}"</p>
                                </div>
                            )}
                            {sistemaEjecucionSummary?.puntuacion_consistencia && (
                                <div className="p-3 bg-gradient-to-r from-[#E8B27A]/10 to-transparent rounded-xl border border-[#E8B27A]/20">
                                    <p className="text-xs font-semibold text-[#E8B27A] uppercase mb-1">Consistencia del sistema</p>
                                    <p className="text-sm text-[#1F2A30] font-medium">{sistemaEjecucionSummary.puntuacion_consistencia}/10</p>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </div>

                {/* Execution System Card */}
                {executionSystem && (
                    <Card className="bg-white border-[#DCE3E8] shadow-sm mb-8" data-testid="execution-system-card">
                        <CardHeader><CardTitle className="flex items-center gap-2 text-[#1F2A30]"><Settings2 className="h-5 w-5 text-[#E8B27A]" />Tu Sistema de Ejecución Completo</CardTitle></CardHeader>
                        <CardContent>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div className="p-4 bg-[#5FAF9D]/5 rounded-xl border border-[#5FAF9D]/20">
                                    <p className="text-xs font-semibold text-[#5FAF9D] uppercase mb-2 flex items-center gap-1"><Minimize2 className="h-3 w-3" />Versión mínima</p>
                                    <p className="text-sm text-[#1F2A30]">{executionSystem.vmv}</p>
                                </div>
                                <div className="p-4 bg-[#3E5F6E]/5 rounded-xl border border-[#3E5F6E]/20">
                                    <p className="text-xs font-semibold text-[#3E5F6E] uppercase mb-2 flex items-center gap-1"><Layout className="h-3 w-3" />Entorno</p>
                                    <p className="text-sm text-[#1F2A30]">{executionSystem.entorno}</p>
                                </div>
                                <div className="p-4 bg-[#E8B27A]/5 rounded-xl border border-[#E8B27A]/20">
                                    <p className="text-xs font-semibold text-[#E8B27A] uppercase mb-2 flex items-center gap-1"><Play className="h-3 w-3" />Disparador</p>
                                    <p className="text-sm text-[#1F2A30]">{executionSystem.disparador}</p>
                                </div>
                                <div className="p-4 bg-[#7A9BA8]/5 rounded-xl border border-[#7A9BA8]/20">
                                    <p className="text-xs font-semibold text-[#7A9BA8] uppercase mb-2 flex items-center gap-1"><Settings2 className="h-3 w-3" />Secuencia</p>
                                    <p className="text-sm text-[#1F2A30]">{executionSystem.secuencia}</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* Final AI Insight */}
                {sistemaEjecucionSummary?.insight_usuario && (
                    <Card className="bg-white border-[#DCE3E8] shadow-sm mb-8">
                        <CardContent className="pt-6">
                            <div className="flex items-start gap-3">
                                <div className="w-10 h-10 bg-[#E8B27A]/20 rounded-full flex items-center justify-center shrink-0">
                                    <Brain className="h-5 w-5 text-[#E8B27A]" />
                                </div>
                                <div>
                                    <p className="text-xs font-semibold text-[#E8B27A] uppercase mb-2">Reflexión final del capítulo</p>
                                    <p className="text-[#1F2A30] italic">"{sistemaEjecucionSummary.insight_usuario}"</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* Back to dashboard */}
                <div className="text-center">
                    <Link to="/usuario">
                        <Button className="bg-[#3E5F6E] hover:bg-[#2F4A57] text-white px-8 py-3 rounded-xl" data-testid="back-to-dashboard-ch5">
                            Volver al inicio <ChevronRight className="h-4 w-4 ml-2" />
                        </Button>
                    </Link>
                </div>
            </main>
            <SENDAFooter />
        </div>
    );
}
